import { useEffect } from 'react';

/**
 * Screen stay-awake is tied to a live hand, not a setting.
 * First deal writes room status PLAYING; Settle Up writes ENDED;
 * the lobby is LOBBY (or missing). No UI — callers pass this boolean.
 */
export function shouldHoldScreenWakeLock(status) {
  return status === 'PLAYING';
}

function wakeLockApi() {
  if (typeof navigator === 'undefined') return null;
  const api = navigator.wakeLock;
  if (!api || typeof api.request !== 'function') return null;
  return api;
}

/**
 * Holds a Screen Wake Lock while `active` is true (a PLAYING game).
 * Feature-detects `navigator.wakeLock.request('screen')` and fails
 * silently when missing or when the request rejects (Low Power Mode).
 * iOS drops the lock when the tab is backgrounded; we re-request on
 * visibilitychange → visible. Cleanup releases on lobby / end / leave / unmount.
 */
export function useScreenWakeLock(active) {
  useEffect(() => {
    if (!active) return undefined;

    const api = wakeLockApi();
    if (!api) return undefined;

    let cancelled = false;
    let sentinel = null;

    const acquire = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      try {
        const next = await api.request('screen');
        if (cancelled) {
          try {
            await next.release();
          } catch (_) {
            /* already released or unsupported */
          }
          return;
        }
        sentinel = next;
      } catch (_) {
        /* NotAllowedError (Low Power Mode), or any other denial */
      }
    };

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        acquire();
      }
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      const held = sentinel;
      sentinel = null;
      if (held && typeof held.release === 'function') {
        Promise.resolve(held.release()).catch(() => {});
      }
    };
  }, [active]);
}
